import * as React from 'react';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from "react-intl";

import '../ResidenceFilterForm/style.scss';

import { A } from '@atoms/LinksAndButtons/A';
import { P } from '@atoms/Text/P';
import { CheckboxList } from '../formComponents/checkboxList';
import { FormRangeSlider } from '../formComponents/FormRangeSlider';
import { Button, ButtonColor, ButtonSize, ButtonType } from '@atoms/LinksAndButtons/Button';
import { ResidenceFilterSection } from '../ResidenceFilterForm/_section';
import { FilterSection } from '../ResidenceFilterForm';
import { getFormattedMessage } from 'features/services/utils/language-helper';
import { useModal } from 'features/modal/hooks/useModal';
import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';
import { PremisesType } from '@pages/PremisesStartPage/types';
import { translatePremiseEnum, filterPremises } from '@pages/PremisesStartPage/helpers';
import {
    selectPremisesMinMaxArea,
    selectPremisesTypes,
    usePremisesStore,
} from 'features/premises/store/usePremisesStore';
import { usePremisesQuery } from 'features/premises/services/PremisesService';
import { AREA_FILTER_MAX, AREA_FILTER_MIN } from 'features/premises/constants';

interface Props {
    scrollToSection?: FilterSection;
}

export const PremisesFilterForm: React.FunctionComponent<Props> = ({ scrollToSection }) => {
    const isMobile = useDeviceStore(selectIsMobile);
    const { close } = useModal();
    const { data: premises = [] } = usePremisesQuery();

    const minMax = usePremisesStore(selectPremisesMinMaxArea);
    const types = usePremisesStore(selectPremisesTypes);
    const setPremisesMinMax = usePremisesStore((state) => state.setPremisesMinMax);
    const setPremisesTypes = usePremisesStore((state) => state.setPremisesTypes);
    const resetPremisesFilters = usePremisesStore((state) => state.resetPremisesFilters);

    const bodyRef = useRef<HTMLDivElement>(null);
    const areaSectionRef = useRef<HTMLDivElement>(null);
    const typeSectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!scrollToSection || !bodyRef.current) return;

        const target = scrollToSection === 'type' ? typeSectionRef.current : areaSectionRef.current;
        if (target && bodyRef.current) {
            bodyRef.current.scrollTop = target.offsetTop - bodyRef.current.offsetTop;
        }
    }, [scrollToSection]);

    const onChangeArea = (values: number[]) => {
        setPremisesMinMax({ min: values[0], max: values[1] });
    };

    const onChangeTypes = (values: string[]) => {
        setPremisesTypes(values as PremisesType[]);
    };

    const handleReset = () => {
        resetPremisesFilters();
        close();
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        close();
    };

    const typeOptions = [
        { value: PremisesType.STORE, label: translatePremiseEnum(PremisesType.STORE) },
        { value: PremisesType.OFFICE, label: translatePremiseEnum(PremisesType.OFFICE) },
        { value: PremisesType.RESTAURANT_CAFE, label: translatePremiseEnum(PremisesType.RESTAURANT_CAFE) },
        { value: PremisesType.GYM, label: translatePremiseEnum(PremisesType.GYM) },
        { value: PremisesType.INDUSTRIAL_WORKSHOP, label: translatePremiseEnum(PremisesType.INDUSTRIAL_WORKSHOP) },
        { value: PremisesType.OFFICE_HOTEL, label: translatePremiseEnum(PremisesType.OFFICE_HOTEL) },
        { value: PremisesType.WAREHOUSE, label: translatePremiseEnum(PremisesType.WAREHOUSE) },
        { value: PremisesType.OTHER, label: translatePremiseEnum(PremisesType.OTHER) },
    ];

    return (
        <form
            onSubmit={handleSubmit}
            className="residence-filter-form"
        >
            <div className='residence-filter-form__header'>
                <P center>{getFormattedMessage("jm.components.ResidenceFilters.filters")}</P>
            </div>

            <div className='residence-filter-form__body' ref={bodyRef}>
                {/* Area */}
                <ResidenceFilterSection
                    ref={areaSectionRef}
                    headline={getFormattedMessage("jm.components.PremisesFilters.premisesArea")}
                >
                    <FormRangeSlider
                        defaultValue={[minMax.min, minMax.max]}
                        min={AREA_FILTER_MIN}
                        max={AREA_FILTER_MAX}
                        minRange={5}
                        valueSuffix="m²"
                        label={null}
                        onSliderChange={onChangeArea}
                    />
                </ResidenceFilterSection>

                {/* Premises type */}
                <ResidenceFilterSection
                    ref={typeSectionRef}
                    headline={getFormattedMessage("jm.components.PremisesFilters.type")}
                >
                    <CheckboxList
                        label={getFormattedMessage("jm.components.PremisesFilters.type")}
                        hideLabel
                        value={types}
                        options={typeOptions}
                        onCustomChange={onChangeTypes}
                    />
                </ResidenceFilterSection>
            </div>

            <div className='residence-filter-form__footer'>
                <span><A
                    href="#"
                    title={getFormattedMessage('jm.components.ResidenceFilters.resetFilters')}
                    onClick={(e) => { e.preventDefault(); handleReset(); }}
                >
                    <FormattedMessage id="jm.components.ResidenceFilters.resetFilters" />
                </A></span>
                <Button
                    buttonColor={ButtonColor.CTA}
                    buttonSize={isMobile
                        ? ButtonSize.FULL
                        : ButtonSize.DEFAULT}
                    buttonType={ButtonType.DEFAULT}
                    submit
                >
                    {getFormattedMessage("jm.components.PremisesFilters.showAll", { number: filterPremises(premises, minMax, types).length })}
                </Button>
            </div>
        </form>
    )
};
